// iter134 + iter143 — /rides/history — list of every ride requested via
// the WhatsApp ride flow. Linked from the VIAJES box on Journey.
// iter143: cards redesigned to match the Journey VIP/VIAJES box style;
// each card is tappable → /rides/:rideId for full detail.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Car, MapPin, Loader2, Star, MessageSquare, RotateCcw } from "lucide-react";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import { sendRideRequest } from "../lib/sendRideRequest";
import RideReviewSheet from "../components/RideReviewSheet";

function fmtFullDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return ""; }
}
function fmtTime(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(lang === "es" ? "es-ES" : "en-US", {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
}

export default function RidesHistory() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewRideId, setReviewRideId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      const cid = (() => {
        try { return localStorage.getItem("habano.client_id") || ""; } catch { return ""; }
      })();
      try {
        const { data } = await api.get(`/rides/mine${me ? "" : `?client_id=${encodeURIComponent(cid)}`}`);
        if (!cancelled) setRides(data?.rides || []);
      } catch {
        if (!cancelled) setRides([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main data-testid="rides-history-page" className="min-h-screen pb-28" style={{ background: "#0B0B0B" }}>
      <header className="px-5 flex items-center gap-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 28px)", paddingBottom: 16 }}>
        <button onClick={() => navigate(-1)} data-testid="rides-history-back" aria-label="Back"
          className="grid place-items-center" style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8" }}>
          {lang === "es" ? "Mis Viajes" : "My Rides"}
        </h1>
      </header>

      {loading ? (
        <div className="px-5 mt-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40" />
        </div>
      ) : rides.length === 0 ? (
        <div className="px-8 mt-20 text-center" data-testid="rides-history-empty">
          <div className="mx-auto mb-4 grid place-items-center"
            style={{ width: 56, height: 56, borderRadius: 999, border: "1px solid rgba(232,184,75,0.35)" }}>
            <Car className="w-6 h-6 text-gold" strokeWidth={1.5} />
          </div>
          <p className="text-foreground/70 max-w-xs mx-auto"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, lineHeight: 1.5 }}>
            {lang === "es"
              ? "Sin viajes — solicita tu primer viaje"
              : "No rides yet — request your first ride"}
          </p>
          <Link
            to="/car"
            data-testid="rides-history-cta"
            className="inline-flex items-center justify-center mt-6"
            style={{
              padding: "12px 24px",
              background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
              color: "#0B0B0B",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              minHeight: 48,
            }}
          >
            {lang === "es" ? "Pedir Transporte" : "Request a Ride"}
          </Link>
        </div>
      ) : (
        <ul className="px-5 mt-2 space-y-3" data-testid="rides-history-list">
          {rides.map((r) => (
            <li key={r.id}>
              <button type="button"
                data-testid={`ride-card-${r.id}`}
                onClick={() => navigate(`/rides/${r.id}`)}
                className="w-full text-left"
                style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  background: "#141414",
                  border: "0.5px solid rgba(232,184,75,0.2)",
                  borderRadius: 12,
                  padding: "16px 16px 14px",
                  minHeight: 88,
                }}>
                <span className="grid place-items-center shrink-0"
                  style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(232,184,75,0.12)" }}>
                  <Car className="w-5 h-5" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div data-testid={`ride-card-${r.id}-name`} className="flex-1 min-w-0"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8",
                               fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.destination_name}
                    </div>
                    <span data-testid={`ride-card-${r.id}-status`}
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#E8B84B",
                               letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700,
                               border: "1px solid rgba(232,184,75,0.4)", borderRadius: 9999,
                               padding: "3px 8px", whiteSpace: "nowrap" }}>
                      {r.status === "completed"
                        ? (lang === "es" ? "Completado" : "Completed")
                        : (lang === "es" ? "Solicitado" : "Requested")}
                    </span>
                  </div>
                  <div className="mt-1" data-testid={`ride-card-${r.id}-datetime`}
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}>
                    {fmtFullDate(r.created_at, lang)}
                    {" · "}
                    {fmtTime(r.created_at, lang)}
                  </div>
                  {r.destination_address && (
                    <div className="mt-1 inline-flex items-center gap-1" data-testid={`ride-card-${r.id}-address`}
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3",
                               whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                      <MapPin className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.destination_address}</span>
                    </div>
                  )}
                  {/* Rating row — inline rating OR Rate Ride link + Ride Again */}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {r.review_rating ? (
                      <span data-testid={`ride-card-${r.id}-rating`}
                        className="inline-flex items-center gap-1"
                        style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif",
                                 fontSize: 14, fontWeight: 600 }}>
                        <Star className="w-4 h-4" fill="#E8B84B" strokeWidth={0} />
                        {r.review_rating}/5
                      </span>
                    ) : (
                      <span data-testid={`ride-card-${r.id}-rate-link`}
                        role="link"
                        onClick={(e) => { e.stopPropagation(); setReviewRideId(r.id); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault(); e.stopPropagation(); setReviewRideId(r.id);
                          }
                        }}
                        tabIndex={0}
                        className="inline-flex items-center gap-1 cursor-pointer"
                        style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif",
                                 fontSize: 14, fontWeight: 600 }}>
                        <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {lang === "es" ? "Calificar viaje" : "Rate Ride"}
                      </span>
                    )}
                    {/* iter161 — Ride Again link. Plain gold text, same
                        weight/size as Rate Ride. Fires the existing
                        sendRideRequest() with the past ride's
                        destination + phone target so the WhatsApp
                        action sheet opens exactly like a fresh
                        request. */}
                    <span data-testid={`ride-card-${r.id}-ride-again`}
                      role="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        sendRideRequest(
                          {
                            name: r.destination_name || (lang === "es" ? "Destino" : "Destination"),
                            address: r.destination_address || "",
                            lat: r.destination_lat,
                            lng: r.destination_lng,
                          },
                          { source: "rides-history-repeat", phoneTarget: r.phone_target },
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault(); e.stopPropagation();
                          sendRideRequest(
                            {
                              name: r.destination_name || (lang === "es" ? "Destino" : "Destination"),
                              address: r.destination_address || "",
                              lat: r.destination_lat,
                              lng: r.destination_lng,
                            },
                            { source: "rides-history-repeat", phoneTarget: r.phone_target },
                          );
                        }
                      }}
                      tabIndex={0}
                      className="inline-flex items-center gap-1 cursor-pointer"
                      style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif",
                               fontSize: 14, fontWeight: 600 }}>
                      <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {lang === "es" ? "Viajar de nuevo" : "Ride Again"}
                    </span>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <RideReviewSheet
        isOpen={!!reviewRideId}
        rideId={reviewRideId}
        onClose={() => setReviewRideId(null)}
        onSubmitted={(updated) => {
          if (updated) setRides((rs) => rs.map((r) => r.id === updated.id ? updated : r));
        }}
      />

      <BottomNav />
    </main>
  );
}
